/**
 * Load something once (and again on demand). A failed reload keeps the last good
 * data and sets `error` beside it: the same rule as the snapshot, never zeros.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BotOpsHttpError } from '../api/botOpsClient';

export interface QueryState<T> { data: T | null; error: BotOpsHttpError | null; loading: boolean; reload: () => void }

export function useQuery<T>(fetcher: () => Promise<{ data: T | null }>, deps: unknown[] = []): QueryState<T> {
  const [state, setState] = useState<{ data: T | null; error: BotOpsHttpError | null; loading: boolean }>({ data: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    Promise.resolve().then(() => fetchRef.current()).then(
      (r) => { if (!cancelled) setState({ data: r.data, error: null, loading: false }); },
      (e) => {
        if (cancelled) return;
        const err = e instanceof BotOpsHttpError ? e : new BotOpsHttpError((e as Error).message || 'Unknown error', 0, 'UNKNOWN');
        setState((s) => ({ data: s.data, error: err, loading: false }));
      },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
